import * as XLSX from "xlsx";

const XlsxPopulate = require("xlsx-populate");

const FILE_TYPE = "application/octet-stream";

const ALPHABET: { [number: number]: string; } = {
  1: "A",
  2: "B",
  3: "C",
  4: "D",
  5: "E",
  6: "F",
  7: "G",
  8: "H",
  9: "I",
  10: "J",
  11: "K",
  12: "L",
  13: "M",
  14: "N",
  15: "O",
  16: "P",
  17: "Q",
  18: "R",
  19: "S",
  20: "T",
  21: "U",
  22: "V",
  23: "W",
  24: "X",
  25: "Y",
  26: "Z",
};

const getRowRange = (position: number, numberOfColumns: number) => {
  return `${ALPHABET[1]}${position}:${ALPHABET[numberOfColumns]}${position}`;
};

const getColumnRange = (position: number, numberOfRows: number) => {
  return `${ALPHABET[position]}2:${ALPHABET[position]}${numberOfRows + 1}`;
};

const downloadFile = (downloadURL: string, fileName: string) => {
  const downloadLink = document.createElement("a");
  document.body.appendChild(downloadLink);
  downloadLink.download = fileName;
  downloadLink.href = downloadURL;
  downloadLink.target = "_blank"; // Must open in new window to prevent subsequent calls failing before this download finishes.
  downloadLink.click();
  setTimeout(() => {
    // Some browsers require waiting to remove child.
    downloadLink.parentNode?.removeChild(downloadLink);
  }, 0);
};

const getColumnsWidth = (fileData: any[], max: number, min: number) => {
  if (fileData.length === 0) return [];
  const result = [];
  const fields = Object.keys(fileData[0]);
  for (const field of fields) {
    result.push({
      wch: Math.max(
        ...fileData.map((item) =>
          item[field].length > max
            ? max
            : item[field].length < min
              ? min
              : item[field].length
        )
      ),
    });
  }
  return result;
};

const addStyles = (workbookBLob: Blob, dataInfo: any, addStyles = true) => {
  return XlsxPopulate.fromDataAsync(workbookBLob).then((workbook: any) => {
    workbook.sheets().forEach((sheet: any) => {
      const { fileRange, headerRange, productRange, fullRange } = dataInfo;
      // style headers
      sheet.range(headerRange).style({
        fill: "a9a9a9",
        bold: true,
      });
      if (addStyles) {
        // style products column
        if (productRange) {
          sheet.range(productRange).style({
            wrapText: true,
          });
        }
        // style files column
        if (fileRange) {
          sheet.range(fileRange).style({
            horizontalAlignment: "left",
          });
        }
        // style all cells
        sheet.range(fullRange).style({
          verticalAlignment: "center",
        });
      }
    });
    return workbook
      .outputAsync()
      .then((workbookBLob: any) => URL.createObjectURL(workbookBLob));
  });
};

const s2ab = (s: string) => {
  const buf = new ArrayBuffer(s.length);
  const view = new Uint8Array(buf);
  for (let i = 0; i !== s.length; i++) {
    view[i] = s.charCodeAt(i);
  }
  return buf;
};

const workbook2blob = (wookbook: XLSX.WorkBook) => {
  const wopts: any = {
    bookType: "xlsx",
    type: "binary",
  };
  const wbout = XLSX.write(wookbook, wopts);
  const blob = new Blob([s2ab(wbout)], {
    type: FILE_TYPE,
  });
  return blob;
};

/**
 * Export a report to Excel.
 *
 * @param {any[]} fileHeader the excel column names.
 * @param {any[]} fileData the excel row data.
 * @param {string} fileName name of the exported file.
 * @return {void} export a report to Excel.
 */

const excelDownload = (
  fileHeader: string[],
  fileData: any[],
  fileName: string
) => {
  const ws: any = XLSX.utils.book_new();
  XLSX.utils.sheet_add_aoa(ws, [fileHeader]);
  ws["!cols"] = getColumnsWidth(fileData, 40, 15);
  XLSX.utils.sheet_add_json(ws, fileData, {
    origin: `${ALPHABET[1]}2`,
    skipHeader: true,
  });

  const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
  const workbookBlob = workbook2blob(wb);
  let ranges: any = {
    headerRange: getRowRange(1, fileHeader.length),
    fileRange: getColumnRange(1, fileData.length),
    fullRange: `A1:${ALPHABET[fileHeader.length]}${fileData.length + 1}`,
  };

  const productPosition = fileHeader.indexOf("Products") + 1;
  if (productPosition) {
    const productRange = getColumnRange(productPosition, fileData.length);
    ranges = {
      ...ranges,
      productRange,
    };
  }

  // max size of bytes to apply all styles to work around a bug with the XLS export library.
  const maxSizeBytes = 3000000;
  addStyles(workbookBlob, ranges, workbookBlob.size < maxSizeBytes).then((url: string) => {
    downloadFile(url, fileName);
  });
};

export { excelDownload, downloadFile };
